import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

function AdminDashboard() {
  const navigate = useNavigate();

  const [backendUrl, setBackendUrl] = useState("");

  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [instructors, setInstructors] = useState([]);

  // ================== ENROLLMENT ==================
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState("");

  // ================== CREATE & ASSIGN SESSION ==================
  const [selectedSessionCourse, setSelectedSessionCourse] = useState("");
  const [selectedInstructor, setSelectedInstructor] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionStartDate, setSessionStartDate] = useState("");
  const [sessionEndDate, setSessionEndDate] = useState("");
  const [sessionLocation, setSessionLocation] = useState("");

  const [enrollStatus, setEnrollStatus] = useState("");
  const [sessionStatus, setSessionStatus] = useState("");

  // ================== ADD COURSE ==================
  const [newCourseID, setNewCourseID] = useState("");
  const [newCourseName, setNewCourseName] = useState("");
  const [newDuration, setNewDuration] = useState("");
  const [courseStatus, setCourseStatus] = useState("");

  const [formData, setFormData] = useState({
    start_date: ""
  });

  // ================== LOAD SESSIONS FOR COURSE ==================
  const fetchSessions = async (courseId) => {
    if (!courseId) {
      setSessions([]);
      setSelectedSession("");
      return;
    }

    try {
      const res = await fetch(
        `${backendUrl}/course-sessions/${courseId}`
      );

      if (!res.ok) {
        throw new Error("Failed to fetch sessions");
      }

      const data = await res.json();

      setSessions(data);
      setSelectedSession("");
    } catch (err) {
      console.error("❌ Failed to fetch sessions:", err);
      setSessions([]);
      setSelectedSession("");
    }
  };

  // ================== LOAD BACKEND URL ==================
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "/config.js";

    script.onload = () => {
      if (window.env?.BACKEND_URL) {
        setBackendUrl(window.env.BACKEND_URL);
      }
    };

    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // ================== FETCH DATA ==================
  const fetchCourses = async () => {
    try {
      const res = await fetch(`${backendUrl}/courses`);
      setCourses(await res.json());
    } catch (err) {
      console.error("❌ Failed to fetch courses:", err);
    }
  };

  const fetchStudents = async () => {
    try {
      const res = await fetch(`${backendUrl}/students`);
      setStudents(await res.json());
    } catch (err) {
      console.error("❌ Failed to fetch students:", err);
    }
  };

  const fetchInstructors = async () => {
    try {
      const res = await fetch(
        `${backendUrl}/users?role=Instructor`
      );

      setInstructors(await res.json());
    } catch (err) {
      console.error("❌ Failed to fetch instructors:", err);
    }
  };

  useEffect(() => {
    if (backendUrl) {
      fetchCourses();
      fetchStudents();
      fetchInstructors();
    }
  }, [backendUrl]);

  // ================== ADD COURSE ==================
  const handleAddCourse = async () => {
    if (!newCourseID || !newCourseName || !newDuration) {
      setCourseStatus(
        "⚠️ Please enter all course details."
      );
      return;
    }

    try {
      const res = await fetch(
        `${backendUrl}/api/courses/add`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            Course_ID: newCourseID,
            Course_Name: newCourseName,
            DurationDays: Number(newDuration)
          })
        }
      );

      const data = await res.json();

      setCourseStatus(
        res.ok
          ? "✅ Course added successfully."
          : `❌ ${data.message}`
      );

      if (res.ok) {
        setNewCourseID("");
        setNewCourseName("");
        setNewDuration("");

        fetchCourses();
      }
    } catch (err) {
      console.error("❌ Add course error:", err);

      setCourseStatus(
        "❌ Server error while adding course."
      );
    }
  };

  // ================== CREATE & ASSIGN SESSION ==================
  const handleCreateSession = async () => {
    if (
      !selectedSessionCourse ||
      !selectedInstructor ||
      !sessionStartDate ||
      !sessionEndDate
    ) {
      setSessionStatus(
        "⚠️ Course, Instructor, Start Date, and End Date are required."
      );
      return;
    }

    if (
      new Date(sessionEndDate) <
      new Date(sessionStartDate)
    ) {
      setSessionStatus(
        "⚠️ End Date cannot be before Start Date."
      );
      return;
    }

    try {
      const res = await fetch(
        `${backendUrl}/api/admin/assign-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            course_id: Number(selectedSessionCourse),
            instructor_id: Number(selectedInstructor),
            start_date: sessionStartDate,
            end_date: sessionEndDate,
            session_name: sessionName,
            location: sessionLocation
          })
        }
      );

      const data = await res.json();

      if (res.ok) {
        setSessionStatus(
          "✅ Session created and instructor assigned successfully."
        );

        // Clear session form
        setSelectedSessionCourse("");
        setSelectedInstructor("");
        setSessionName("");
        setSessionStartDate("");
        setSessionEndDate("");
        setSessionLocation("");
      } else {
        setSessionStatus(
          `❌ ${data.message || "Failed to create session."}`
        );
      }
    } catch (err) {
      console.error(
        "❌ Session creation/assignment error:",
        err
      );

      setSessionStatus(
        "❌ Server error while creating session."
      );
    }
  };

  // ================== ENROLL STUDENT ==================
  const handleEnroll = async () => {
    if (
      !selectedStudent ||
      !selectedCourse ||
      !selectedSession
    ) {
      setEnrollStatus(
        "⚠️ Select course, session and student."
      );
      return;
    }

    try {
      const res = await fetch(
        `${backendUrl}/api/enrollment/enroll`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            user_id: Number(selectedStudent),
            course_id: Number(selectedCourse),
            session_id: Number(selectedSession),
            start_date: formData.start_date
          })
        }
      );

      const data = await res.json();

      setEnrollStatus(
        res.ok
          ? "✅ Student enrolled."
          : `❌ ${data.message}`
      );

      if (res.ok) {
        setSelectedCourse("");
        setSelectedSession("");
        setSelectedStudent("");
        setSessions([]);
        setFormData({
          start_date: ""
        });
      }
    } catch (err) {
      console.error("❌ Enrollment error:", err);

      setEnrollStatus(
        "❌ Server error while enrolling student."
      );
    }
  };

  return (
    <div style={dashboardContainer}>

      {/* ================== TOP NAV ================== */}
      <section style={navSectionStyle}>

        <button
          style={navBtnStyle}
          onClick={() =>
            navigate("/admin/upload")
          }
        >
          Upload Materials
        </button>

        {/* Create Sessions button REMOVED */}

        <button
          style={navBtnStyle}
          onClick={() =>
            navigate("/admin/release-material")
          }
        >
          Release Materials
        </button>

        <button
          style={navBtnStyle}
          onClick={() =>
            navigate("/admin/assign-exam")
          }
        >
          Assign Validity
        </button>

        <button
          style={navBtnStyle}
          onClick={() =>
            navigate("/admin/exams")
          }
        >
          Upload Exams
        </button>

        <button
          style={navBtnStyle}
          onClick={() =>
            navigate("/admin/results")
          }
        >
          View Results
        </button>

      </section>

      <div style={twoColumnWrapper}>

        {/* ================== ADD COURSE ================== */}
        <section style={sideSection}>

          <h2 style={sectionTitle}>
            ➕ Add Course
          </h2>

          <input
            placeholder="Course ID"
            value={newCourseID}
            onChange={(e) =>
              setNewCourseID(e.target.value)
            }
            style={inputStyleFull}
          />

          <input
            placeholder="Course Name"
            value={newCourseName}
            onChange={(e) =>
              setNewCourseName(e.target.value)
            }
            style={inputStyleFull}
          />

          <input
            type="number"
            placeholder="Duration (Days)"
            value={newDuration}
            onChange={(e) =>
              setNewDuration(e.target.value)
            }
            style={inputStyleFull}
          />

          <button
            onClick={handleAddCourse}
            style={buttonStyle}
          >
            Add Course
          </button>

          {courseStatus && (
            <p>{courseStatus}</p>
          )}

        </section>


        {/* ================== CREATE & ASSIGN SESSION ================== */}
        <section style={sideSection}>

          <h2 style={sectionTitle}>
            🎓 Create & Assign Session
          </h2>

          {/* Course */}
          <select
            value={selectedSessionCourse}
            onChange={(e) =>
              setSelectedSessionCourse(
                e.target.value
              )
            }
            style={inputStyleFull}
          >
            <option value="">
              -- Select Course --
            </option>

            {courses.map((c) => (
              <option
                key={c.course_id}
                value={c.course_id}
              >
                {c.course_name}
              </option>
            ))}
          </select>

          {/* Session Name */}
          <input
            type="text"
            placeholder="Session Name"
            value={sessionName}
            onChange={(e) =>
              setSessionName(e.target.value)
            }
            style={inputStyleFull}
          />

          {/* Instructor */}
          <select
            value={selectedInstructor}
            onChange={(e) =>
              setSelectedInstructor(
                e.target.value
              )
            }
            style={inputStyleFull}
          >
            <option value="">
              -- Select Instructor --
            </option>

            {instructors.map((i) => (
              <option
                key={i.User_ID}
                value={i.User_ID}
              >
                {i.Username}
              </option>
            ))}
          </select>

          {/* Start Date */}
          <input
            type="date"
            value={sessionStartDate}
            onChange={(e) =>
              setSessionStartDate(
                e.target.value
              )
            }
            style={inputStyleFull}
          />

          {/* End Date */}
          <input
            type="date"
            value={sessionEndDate}
            onChange={(e) =>
              setSessionEndDate(
                e.target.value
              )
            }
            style={inputStyleFull}
          />

          {/* Location */}
          <input
            type="text"
            placeholder="Location"
            value={sessionLocation}
            onChange={(e) =>
              setSessionLocation(
                e.target.value
              )
            }
            style={inputStyleFull}
          />

          <button
            onClick={handleCreateSession}
            style={buttonStyle}
          >
            Create & Assign Session
          </button>

          {sessionStatus && (
            <p>{sessionStatus}</p>
          )}

        </section>


        {/* ================== ENROLL STUDENT ================== */}
        <section style={sideSection}>

          <h2 style={sectionTitle}>
            ➕ Enroll Student
          </h2>

          {/* Course */}
          <select
            value={selectedCourse}
            onChange={(e) => {
              const courseId = e.target.value;

              setSelectedCourse(courseId);

              // Load sessions belonging to this course
              fetchSessions(courseId);
            }}
            style={inputStyleFull}
          >
            <option value="">
              -- Select Course --
            </option>

            {courses.map((c) => (
              <option
                key={c.course_id}
                value={c.course_id}
              >
                {c.course_name}
              </option>
            ))}
          </select>


          {/* Session */}
          <select
            value={selectedSession}
            onChange={(e) =>
              setSelectedSession(
                e.target.value
              )
            }
            style={inputStyleFull}
            disabled={!selectedCourse}
          >
            <option value="">
              -- Select Session --
            </option>

            {sessions.map((s) => (
              <option
                key={s.Session_ID}
                value={s.Session_ID}
              >
                {s.SessionName}
              </option>
            ))}
          </select>


          {/* Student */}
          <select
            value={selectedStudent}
            onChange={(e) =>
              setSelectedStudent(
                e.target.value
              )
            }
            style={inputStyleFull}
          >
            <option value="">
              -- Select Student --
            </option>

            {students.map((s) => (
              <option
                key={s.User_ID}
                value={s.User_ID}
              >
                {s.Username}
              </option>
            ))}
          </select>


          {/* Start Date */}
          <input
            type="date"
            value={formData.start_date}
            onChange={(e) =>
              setFormData({
                start_date: e.target.value
              })
            }
            style={inputStyleFull}
          />


          {/* Enroll */}
          <button
            onClick={handleEnroll}
            style={buttonStyle}
          >
            Enroll Student
          </button>

          {enrollStatus && (
            <p>{enrollStatus}</p>
          )}

        </section>

      </div>
    </div>
  );
}

export default AdminDashboard;


/* ================== STYLES (UNCHANGED) ================== */

const dashboardContainer = {
  maxWidth: "1250px",
  margin: "20px auto",
  padding: "20px"
};

const navSectionStyle = {
  display: "flex",
  gap: "15px",
  marginBottom: "25px",
  flexWrap: "wrap"
};

const navBtnStyle = {
  padding: "12px",
  backgroundColor: "#C70013",
  color: "white",
  border: "none",
  borderRadius: "8px"
};

const twoColumnWrapper = {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit, minmax(350px, 1fr))",
  gap: "25px"
};

const sideSection = {
  backgroundColor: "#f7f7f7",
  padding: "20px",
  borderRadius: "10px"
};

const sectionTitle = {
  fontSize: "22px",
  color: "#C70013",
  fontWeight: 700
};

const inputStyleFull = {
  width: "96%",
  padding: "12px",
  marginBottom: "15px"
};

const buttonStyle = {
  padding: "12px",
  backgroundColor: "#C70013",
  color: "white",
  border: "none",
  borderRadius: "7px"
};